import { offlineStorage } from './offlineStorage';

class SyncService {
  private syncInProgress = false;
  private syncInterval: NodeJS.Timeout | null = null;

  /**
   * Start automatic background sync
   */
  startAutoSync(intervalMs: number = 30000): void {
    if (this.syncInterval) {
      return;
    }

    console.log('🔄 SyncService: Starting auto-sync...');

    // Immediate sync
    this.syncPendingActions();

    // Periodic sync
    this.syncInterval = setInterval(() => {
      if (navigator.onLine) {
        this.syncPendingActions();
      }
    }, intervalMs);

    // Sync when coming back online
    window.addEventListener('online', () => {
      console.log('🌐 SyncService: Connection restored, syncing...');
      this.syncPendingActions();
    });
  }

  /**
   * Stop automatic background sync
   */
  stopAutoSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
      console.log('🔄 SyncService: Auto-sync stopped');
    }
  }

  /**
   * Sync all pending actions with the server
   */
  async syncPendingActions(): Promise<void> {
    if (this.syncInProgress) {
      console.log('⏳ SyncService: Sync already in progress, skipping...');
      return;
    }

    if (!navigator.onLine) {
      console.log('📴 SyncService: Offline, skipping sync');
      return;
    }

    this.syncInProgress = true;

    try {
      await offlineStorage.initialize();
      const pendingActions = await offlineStorage.getPendingActions();

      if (pendingActions.length === 0) {
        console.log('✅ SyncService: No pending actions to sync');
        return;
      }

      console.log(`🔄 SyncService: Syncing ${pendingActions.length} pending actions...`);

      let successCount = 0;
      let failureCount = 0;

      for (const action of pendingActions) {
        try {
          await this.executeAction(action);
          await offlineStorage.deletePendingAction(action.id);
          successCount++;
          console.log(`✅ SyncService: Synced action ${action.id}`);
        } catch (error) {
          failureCount++;
          console.error(`❌ SyncService: Failed to sync action ${action.id}:`, error);

          // Update retry count
          await offlineStorage.updatePendingActionRetryCount(
            action.id,
            action.retryCount + 1
          );

          // If too many retries, remove the action
          if (action.retryCount >= 5) {
            console.error(`❌ SyncService: Removing action ${action.id} after too many retries`);
            await offlineStorage.deletePendingAction(action.id);
          }
        }
      }

      console.log(
        `✅ SyncService: Sync completed - ${successCount} succeeded, ${failureCount} failed`
      );

      // Update last sync time
      await offlineStorage.setMetadata('lastSyncTime', Date.now());
    } catch (error) {
      console.error('❌ SyncService: Sync failed:', error);
    } finally {
      this.syncInProgress = false;
    }
  }

  /**
   * Execute a single pending action
   */
  private async executeAction(action: any): Promise<void> {
    // This is a placeholder - you'll need to implement actual Firebase operations
    // based on the action type and collection
    console.log(`🔄 SyncService: Executing action:`, action);

    // Example implementation:
    // switch (action.collection) {
    //   case 'watchlists':
    //     if (action.type === 'add') {
    //       await addWatchlistToFirebase(action.data);
    //     } else if (action.type === 'update') {
    //       await updateWatchlistInFirebase(action.data);
    //     } else if (action.type === 'delete') {
    //       await deleteWatchlistFromFirebase(action.data.id);
    //     }
    //     break;
    //   // ... handle other collections
    // }

    // For now, we'll just simulate success
    await new Promise(resolve => setTimeout(resolve, 100));
  }

  /**
   * Queue an action for later sync
   */
  async queueAction(
    type: 'add' | 'update' | 'delete',
    collection: string,
    data: any
  ): Promise<void> {
    await offlineStorage.initialize();
    await offlineStorage.addPendingAction(type, collection, data);

    // Try to sync immediately if online
    if (navigator.onLine) {
      this.syncPendingActions();
    }
  }

  /**
   * Get sync status
   */
  async getSyncStatus(): Promise<{
    lastSyncTime: number | null;
    pendingActionsCount: number;
    isOnline: boolean;
    isSyncing: boolean;
  }> {
    await offlineStorage.initialize();
    const lastSyncTime = await offlineStorage.getMetadata('lastSyncTime');
    const pendingActions = await offlineStorage.getPendingActions();

    return {
      lastSyncTime,
      pendingActionsCount: pendingActions.length,
      isOnline: navigator.onLine,
      isSyncing: this.syncInProgress,
    };
  }

  /**
   * Force a manual sync
   */
  async forceSync(): Promise<void> {
    console.log('🔄 SyncService: Force sync initiated');
    await this.syncPendingActions();
  }

  /**
   * Clear all pending actions
   */
  async clearPendingActions(): Promise<void> {
    await offlineStorage.initialize();
    await offlineStorage.clearPendingActions();
    console.log('🧹 SyncService: All pending actions cleared');
  }
}

// Export singleton instance
export const syncService = new SyncService();
